/**
 * Hooks de pins/favoritos da mensageria condensada (Mensageria Condensada PR B4 / F1a).
 *
 * Consome `GET/POST/DELETE /v1/direct-messages/pins` (gate `direct-message-participate`).
 * A lista é GATEADA por capability no `enabled` (não só no render) — sem a cap o
 * fetch nem dispara (lição "gatear o fetch"). A mutação de pin/unpin invalida a
 * própria lista de pins (a lista de threads/badge não muda com pin).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../../../hooks/use-capability';
import { api } from '../../../../lib/api-client';
import { useToastStore } from '../../../../stores/toast-store';
import { ROOT_KEY } from '../../hooks/use-direct-messages';

import type { PinResult, PinSummary, PinTargetBody, UnpinResult } from '../../types';

export const PINS_KEY = [ROOT_KEY, 'pins'] as const;

const STALE = 30_000;

/** Contexto do `onMutate` — snapshot p/ rollback no `onError`. */
interface PinMutationContext {
  previous: PinSummary[] | undefined;
}

/** Pin otimista local (id sintético; o `onSettled` invalida e o real chega no refetch). */
function optimisticPin(body: PinTargetBody): PinSummary {
  return {
    id: `optimistic_${body.targetType}_${body.targetId}`,
    targetType: body.targetType,
    targetId: body.targetId,
    createdAt: new Date().toISOString(),
    // Rótulo real chega no refetch (onSettled invalida); até lá, vazio → o rail
    // cai no fallback honesto. counterpartUserId só é conhecido p/ pin de pessoa.
    label: '',
    counterpartUserId: body.targetType === 'user' ? body.targetId : null,
  };
}

function sameTarget(a: PinTargetBody, b: PinSummary): boolean {
  return a.targetType === b.targetType && a.targetId === b.targetId;
}

export function usePins(): {
  pins: PinSummary[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const canRead = useCapability('direct-message-participate');
  const query = useQuery({
    queryKey: PINS_KEY,
    queryFn: () => api.listPins(),
    enabled: canRead,
    staleTime: STALE,
  });
  return {
    pins: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * Mutação de pin/unpin. Expõe `pin`/`unpin` (idempotentes no backend) + flag de
 * pendência.
 *
 * A estrela precisa de feedback INSTANTÂNEO: o `createPin`/`deletePin` usa
 * `suppressForbiddenToast` (não toasta sozinho), então sem tratamento o erro era
 * engolido em silêncio. Aqui:
 *   - `onMutate` escreve o estado otimista no cache (a estrela vira na hora) e tira
 *     um snapshot;
 *   - `onError` faz rollback ao snapshot E surface um toast vermelho honesto;
 *   - `onSettled` invalida a lista (o pin real chega no refetch).
 * Correto tanto para `targetType:'thread'` quanto `'user'`.
 */
export function usePinMutation(): {
  pin: (body: PinTargetBody) => void;
  unpin: (body: PinTargetBody) => void;
  isPending: boolean;
} {
  const qc = useQueryClient();

  const surfaceError = (action: 'fixar' | 'desafixar'): void => {
    useToastStore.getState().push({
      variant: 'error',
      title: `Não foi possível ${action}`,
      description: 'Tente novamente em instantes.',
      durationMs: 5000,
    });
  };

  const create = useMutation<PinResult, unknown, PinTargetBody, PinMutationContext>({
    mutationFn: (body: PinTargetBody) => api.createPin(body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: PINS_KEY });
      const previous = qc.getQueryData<PinSummary[]>(PINS_KEY);
      const base = previous ?? [];
      if (!base.some((p) => sameTarget(body, p))) {
        qc.setQueryData<PinSummary[]>(PINS_KEY, [...base, optimisticPin(body)]);
      }
      return { previous };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData<PinSummary[]>(PINS_KEY, ctx.previous);
      surfaceError('fixar');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: PINS_KEY });
    },
  });

  const remove = useMutation<UnpinResult, unknown, PinTargetBody, PinMutationContext>({
    mutationFn: (body: PinTargetBody) => api.deletePin(body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: PINS_KEY });
      const previous = qc.getQueryData<PinSummary[]>(PINS_KEY);
      const base = previous ?? [];
      qc.setQueryData<PinSummary[]>(
        PINS_KEY,
        base.filter((p) => !sameTarget(body, p)),
      );
      return { previous };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData<PinSummary[]>(PINS_KEY, ctx.previous);
      surfaceError('desafixar');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: PINS_KEY });
    },
  });

  return {
    pin: (body: PinTargetBody) => {
      create.mutate(body);
    },
    unpin: (body: PinTargetBody) => {
      remove.mutate(body);
    },
    isPending: create.isPending || remove.isPending,
  };
}
